import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { Event } from '../types';

const EventsPage: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'my' | 'public'>('my');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadEvents = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (filter === 'my') {
        params.creator_id = user?.sub;
      } else if (filter === 'public') {
        params.status = 'public';
      }

      const response = await apiClient.get('/v1/events', { params });
      setEvents(response.data.data?.events || []);
    } catch (err: any) {
      console.error('Failed to load events:', err);
      setError(err.response?.data?.error?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [filter, user?.sub]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleCreateEvent = () => {
    setShowCreateModal(true);
  };

  const handleEventCreated = () => {
    setShowCreateModal(false);
    loadEvents();
  };

  return (
    <div className="container mt-4">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h1>
              <i className="fas fa-calendar me-2"></i>
              Events
            </h1>
            <button
              className="btn btn-primary"
              onClick={handleCreateEvent}
            >
              <i className="fas fa-plus me-2"></i>
              Create Event
            </button>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'my' ? 'active' : ''}`}
                onClick={() => setFilter('my')}
              >
                <i className="fas fa-user me-1"></i>
                My Events
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                <i className="fas fa-list me-1"></i>
                All Events
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'public' ? 'active' : ''}`}
                onClick={() => setFilter('public')}
              >
                <i className="fas fa-globe me-1"></i>
                Public Events
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Events List */}
      <div className="row">
        <div className="col-12">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading events...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          ) : events.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                <h5>No Events Found</h5>
                <p className="text-muted">
                  {filter === 'my'
                    ? "You haven't created any events yet."
                    : 'No events available.'}
                </p>
                {filter === 'my' && (
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateEvent}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Create Your First Event
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="row">
              {events.map((event) => (
                <div key={event.event_id} className="col-md-6 col-lg-4 mb-4">
                  <EventCard event={event} onUpdate={loadEvents} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleEventCreated}
        />
      )}
    </div>
  );
};

interface EventCardProps {
  event: Event;
  onUpdate: () => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onUpdate }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isSingleDay = event.start_date === event.end_date;

  return (
    <div className="card h-100">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <h5 className="card-title mb-0">{event.name}</h5>
          <span className={`badge ${event.status === 'public' ? 'bg-success' : 'bg-secondary'}`}>
            {event.status === 'public' ? 'Public' : 'Draft'}
          </span>
        </div>

        <div className="mb-2">
          <small className="text-muted">
            <i className="fas fa-calendar me-1"></i>
            {isSingleDay
              ? formatDate(event.start_date)
              : `${formatDate(event.start_date)} - ${formatDate(event.end_date)}`}
          </small>
        </div>

        <div className="mb-2">
          <small className="text-muted">
            <i className="fas fa-map-marker-alt me-1"></i>
            {event.location}
          </small>
        </div>

        <p className="card-text text-muted small">
          {event.description.length > 100
            ? `${event.description.substring(0, 100)}...`
            : event.description}
        </p>

        {event.co_organizers && event.co_organizers.length > 0 && (
          <div className="mb-2">
            <small className="text-muted">
              <i className="fas fa-users me-1"></i>
              {event.co_organizers.length} co-organizer{event.co_organizers.length !== 1 ? 's' : ''}
            </small>
          </div>
        )}
      </div>

      <div className="card-footer bg-transparent">
        <Link
          to={`/events/${event.event_id}`}
          className="btn btn-sm btn-outline-primary w-100"
        >
          <i className="fas fa-eye me-1"></i>
          View Details
        </Link>
      </div>
    </div>
  );
};

interface CreateEventModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    location: '',
    description: '',
    status: 'draft' as 'draft' | 'public'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Event name is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate < startDate) {
        newErrors.end_date = 'End date cannot be before start date';
      }
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      await apiClient.post('/v1/events', formData);
      onSuccess();
    } catch (err: any) {
      console.error('Failed to create event:', err);
      setSubmitError(
        err.response?.data?.error?.message || 'Failed to create event. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-calendar-plus me-2"></i>
              Create New Event
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={submitting}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {submitError && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {submitError}
                </div>
              )}

              <div className="mb-3">
                <label htmlFor="name" className="form-label">
                  Event Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="e.g., TechConf 2024"
                />
                {errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="start_date" className="form-label">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  {errors.start_date && (
                    <div className="invalid-feedback">{errors.start_date}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="end_date" className="form-label">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${errors.end_date ? 'is-invalid' : ''}`}
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  {errors.end_date && (
                    <div className="invalid-feedback">{errors.end_date}</div>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="location" className="form-label">
                  Location <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="e.g., London, UK"
                />
                {errors.location && (
                  <div className="invalid-feedback">{errors.location}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="description" className="form-label">
                  Description <span className="text-danger">*</span>
                </label>
                <textarea
                  className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                  id="description"
                  name="description"
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="Describe your event..."
                ></textarea>
                {errors.description && (
                  <div className="invalid-feedback">{errors.description}</div>
                )}
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="status"
                    name="status"
                    checked={formData.status === 'public'}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 'public' : 'draft' }))}
                    disabled={submitting}
                  />
                  <label className="form-check-label" htmlFor="status">
                    Make this event public
                  </label>
                  <div className="form-text">
                    Public events are visible to all users. Draft events are only visible to you and co-organizers.
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check me-2"></i>
                    Create Event
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
